import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { catchError, map } from "rxjs/operators";

import { ApiResponse, PaginatedApiResponse } from "@lib/types";
import { BaseApiService } from "@app/shared/services/base-api.service";

import {
  UploadDocumentDto,
  UploadDocumentResponseDto,
  Document,
} from "@shared/models";
import { DocumentFilterOptionsDto } from "@shared/models/document-filter-options.dto";
import { DocumentsQueryParams } from "../models/documents-query-params.dto";

@Injectable({
  providedIn: "root",
})
export class DocumentsApiService extends BaseApiService {
  protected readonly endpoint = "/document";
  protected readonly microservice = "orderWorkflow" as const;

  /**
   * POST /api/document/upload
   */
  uploadDocument(
    request: UploadDocumentDto,
  ): Observable<UploadDocumentResponseDto> {
    const formData = new FormData();

    formData.append("file", request.file, request.file.name);

    if (request.moduleType) {
      formData.append("moduleType", request.moduleType);
    }
    if (request.invoiceId !== null) {
      formData.append("invoiceId", request.invoiceId.toString());
    }
    if (request.orderId !== null) {
      formData.append("orderId", request.orderId.toString());
    }
    if (request.loanId !== null) {
      formData.append("loanId", request.loanId.toString());
    }
    if (request.eventId !== null) {
      formData.append("eventId", request.eventId.toString());
    }
    if (request.documentType) {
      formData.append("documentType", request.documentType);
    }
    if (request.source !== null) {
      formData.append("source", request.source);
    }
    if (request.description !== null) {
      formData.append("description", request.description);
    }

    const url = `${this.apiUrl}/upload`;

    return this.http
      .post<ApiResponse<UploadDocumentResponseDto>>(url, formData)
      .pipe(
        map((response: ApiResponse<UploadDocumentResponseDto>) => {
          if (!response.success) {
            throw new Error(response.message || "Document upload failed");
          }
          return response.data as UploadDocumentResponseDto;
        }),
        catchError((error) => {
          throw error;
        }),
      );
  }

  /**
   * POST /api/document/bulkupload
   */
  bulkUploadDocuments(
    items: UploadDocumentDto[],
  ): Observable<UploadDocumentResponseDto[]> {
    const formData = new FormData();

    items.forEach((item, index) => {
      formData.append(`items[${index}].file`, item.file, item.file.name);

      if (item.moduleType) {
        formData.append(`items[${index}].moduleType`, item.moduleType);
      }
      if (item.invoiceId !== null) {
        formData.append(`items[${index}].invoiceId`, item.invoiceId.toString());
      }
      if (item.orderId !== null) {
        formData.append(`items[${index}].orderId`, item.orderId.toString());
      }
      if (item.loanId !== null) {
        formData.append(`items[${index}].loanId`, item.loanId.toString());
      }
      if (item.eventId !== null) {
        formData.append(`items[${index}].eventId`, item.eventId.toString());
      }
      if (item.documentType) {
        formData.append(`items[${index}].documentType`, item.documentType);
      }
      if (item.source !== null) {
        formData.append(`items[${index}].source`, item.source);
      }
      if (item.description !== null) {
        formData.append(`items[${index}].description`, item.description);
      }
    });

    const url = `${this.apiUrl}/bulkupload`;

    return this.http
      .post<ApiResponse<UploadDocumentResponseDto[]>>(url, formData)
      .pipe(
        map((response: ApiResponse<UploadDocumentResponseDto[]>) => {
          if (!response.success) {
            throw new Error(response.message || "Bulk document upload failed");
          }
          return response.data as UploadDocumentResponseDto[];
        }),
        catchError((error) => {
          throw error;
        }),
      );
  }

  /**
   * GET /api/document/{loanId}/loan-documents
   */
  getLoanDocuments(
    loanId: number,
    params: DocumentsQueryParams = {},
  ): Observable<PaginatedApiResponse<Document>> {
    const paramsObject: Record<string, unknown> = {};

    if (params.pageIndex !== undefined && params.pageIndex !== null) {
      paramsObject["pageIndex"] = params.pageIndex;
    }

    if (params.pageSize !== undefined && params.pageSize !== null) {
      paramsObject["pageSize"] = params.pageSize;
    }

    if (params.searchText) {
      paramsObject["searchText"] = params.searchText;
    }

    if (params.sortBy) {
      paramsObject["sortBy"] = params.sortBy;
    }

    if (params.sortDirection) {
      paramsObject["sortDirection"] = params.sortDirection;
    }

    if (params.types?.length) {
      paramsObject["types"] = params.types;
    }

    if (params.sources?.length) {
      paramsObject["sources"] = params.sources;
    }

    if (params.uploadedBy?.length) {
      paramsObject["uploadedBy"] = params.uploadedBy;
    }

    const queryParams = this.buildParams(paramsObject);

    return this.getPaginated(`/${loanId}/loan-documents`, {
      params: queryParams,
    });
  }

  /**
   * GET /api/document/{orderId}/order-documents
   */
  getOrderDocuments(
    orderId: number,
    params: DocumentsQueryParams = {},
  ): Observable<PaginatedApiResponse<Document>> {
    const paramsObject: Record<string, unknown> = {};

    if (params.pageIndex !== undefined && params.pageIndex !== null) {
      paramsObject["pageIndex"] = params.pageIndex;
    }

    if (params.pageSize !== undefined && params.pageSize !== null) {
      paramsObject["pageSize"] = params.pageSize;
    }

    if (params.searchText) {
      paramsObject["searchText"] = params.searchText;
    }

    if (params.sortBy) {
      paramsObject["sortBy"] = params.sortBy;
    }

    if (params.sortDirection) {
      paramsObject["sortDirection"] = params.sortDirection;
    }

    if (params.types?.length) {
      paramsObject["types"] = params.types;
    }

    if (params.sources?.length) {
      paramsObject["sources"] = params.sources;
    }

    if (params.uploadedBy?.length) {
      paramsObject["uploadedBy"] = params.uploadedBy;
    }

    const queryParams = this.buildParams(paramsObject);

    return this.getPaginated(`/${orderId}/order-documents`, {
      params: queryParams,
    });
  }

  /**
   * GET /api/document/orders/{orderId}/events/{eventId}/documents
   */
  getOrderEventDocuments(
    orderId: number,
    eventId: number,
    params: DocumentsQueryParams = {},
  ): Observable<PaginatedApiResponse<Document>> {
    const paramsObject: Record<string, unknown> = {};

    if (params.pageIndex !== undefined && params.pageIndex !== null) {
      paramsObject["pageIndex"] = params.pageIndex;
    }

    if (params.pageSize !== undefined && params.pageSize !== null) {
      paramsObject["pageSize"] = params.pageSize;
    }

    if (params.searchText) {
      paramsObject["searchText"] = params.searchText;
    }

    if (params.sortBy) {
      paramsObject["sortBy"] = params.sortBy;
    }

    if (params.sortDirection) {
      paramsObject["sortDirection"] = params.sortDirection;
    }

    if (params.types?.length) {
      paramsObject["types"] = params.types;
    }

    if (params.sources?.length) {
      paramsObject["sources"] = params.sources;
    }

    if (params.uploadedBy?.length) {
      paramsObject["uploadedBy"] = params.uploadedBy;
    }

    const queryParams = this.buildParams(paramsObject);

    return this.getPaginated(`/orders/${orderId}/events/${eventId}/documents`, {
      params: queryParams,
    });
  }

  /**
   * GET /api/document/documents-filter-options
   */
  getDocumentsFilterOptions(): Observable<DocumentFilterOptionsDto> {
    return this.get<DocumentFilterOptionsDto>("/documents-filter-options");
  }

  /**
   * DELETE /api/document/delete/{id}
   */
  deleteDocument(id: number): Observable<void> {
    return this.delete<void>(`/delete/${id}`);
  }

  /**
   * GET /api/document/get-types
   */
  getDocumentTypes(): Observable<string[]> {
    return this.get<string[]>("/get-types");
  }
}
